import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import useLocalStorage from "../hooks/useLocalStorage";
import { useAuth } from "./AuthContext";
import { API_BASE_URL } from "../components/pages/AdminLogin/constants";
import { fetchWithCORS } from "../components/pages/AdminLogin/utils";

// Create users context
const UsersContext = createContext(null);

/**
 * Custom hook to use the users context
 * @returns {Object} Users context value
 */
export const useUsers = () => {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error("useUsers must be used within a UsersProvider");
  }
  return context;
};

/**
 * Users provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const UsersProvider = ({ children }) => {
  // Auth context for token
  const { token, isAuthenticated, getAuthHeaders } = useAuth();

  // Cache users data in localStorage
  const [cachedUsers, setCachedUsers, removeCachedUsers] = useLocalStorage(
    "cached_users",
    []
  );
  const [cachedTimestamp, setCachedTimestamp] = useLocalStorage(
    "users_cache_timestamp",
    null
  );

  // Users state
  const [users, setUsers] = useState(cachedUsers || []);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cache expiration time (12 hours)
  const CACHE_EXPIRATION = 12 * 60 * 60 * 1000;

  /**
   * Check if cache is expired
   * @returns {boolean} True if cache is expired
   */
  const isCacheExpired = useCallback(() => {
    if (!cachedTimestamp) return true;
    return Date.now() - cachedTimestamp > CACHE_EXPIRATION;
  }, [cachedTimestamp]);

  /**
   * Fetch users from API
   * @param {number} page - Page number
   * @param {number} limit - Number of users per page
   * @param {string} search - Search query
   * @returns {Promise<Object>} Fetch result
   */
  const fetchUsers = useCallback(
    async (page = 1, limit = 10, search = "") => {
      if (!isAuthenticated) {
        return { success: false, error: "Not authenticated" };
      }

      setLoading(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams({
          page,
          limit,
          ...(search ? { search } : {}),
        }).toString();

        const response = await fetch(`${API_BASE_URL}/users?${queryParams}`, {
          method: "GET",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch users");
        }

        const data = await response.json();

        setUsers(data.users || []);
        setTotalPages(data.totalPages || 1);
        setCurrentPage(page);

        // Update cache
        setCachedUsers(data.users || []);
        setCachedTimestamp(Date.now());

        setLoading(false);
        return { success: true, data };
      } catch (err) {
        console.error("Fetch users error:", err);
        setError(err.message || "Failed to fetch users");
        setLoading(false);
        return { success: false, error: err.message };
      }
    },
    [isAuthenticated, getAuthHeaders, setCachedUsers, setCachedTimestamp]
  );

  /**
   * Fetch user by ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Fetch result
   */
  const fetchUserById = useCallback(
    async (userId) => {
      if (!isAuthenticated || !userId) {
        return { success: false, error: "Not authenticated or missing ID" };
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
          method: "GET",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch user");
        }

        const data = await response.json();
        setSelectedUser(data.user || null);

        setLoading(false);
        return { success: true, data: data.user };
      } catch (err) {
        console.error("Fetch user error:", err);
        setError(err.message || "Failed to fetch user");
        setLoading(false);
        return { success: false, error: err.message };
      }
    },
    [isAuthenticated, getAuthHeaders]
  );

  /**
   * Create new user
   * @param {Object} userData - User data
   * @returns {Promise<Object>} Create result
   */
  const createUser = useCallback(
    async (userData) => {
      if (!isAuthenticated) {
        return { success: false, error: "Not authenticated" };
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/users`, {
          method: "POST",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create user");
        }

        const data = await response.json();

        // Refresh user list
        fetchUsers(currentPage, pageSize);

        setLoading(false);
        return { success: true, data: data.user };
      } catch (err) {
        console.error("Create user error:", err);
        setError(err.message || "Failed to create user");
        setLoading(false);
        return { success: false, error: err.message };
      }
    },
    [isAuthenticated, getAuthHeaders, currentPage, pageSize, fetchUsers]
  );

  /**
   * Update user
   * @param {string} userId - User ID
   * @param {Object} userData - User data
   * @returns {Promise<Object>} Update result
   */
  const updateUser = useCallback(
    async (userId, userData) => {
      if (!isAuthenticated || !userId) {
        return { success: false, error: "Not authenticated or missing ID" };
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
          method: "PUT",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update user");
        }

        const data = await response.json();

        // Update selected user if it's the same
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser(data.user);
        }

        // Refresh user list
        fetchUsers(currentPage, pageSize);

        setLoading(false);
        return { success: true, data: data.user };
      } catch (err) {
        console.error("Update user error:", err);
        setError(err.message || "Failed to update user");
        setLoading(false);
        return { success: false, error: err.message };
      }
    },
    [
      isAuthenticated,
      getAuthHeaders,
      selectedUser,
      currentPage,
      pageSize,
      fetchUsers,
    ]
  );

  /**
   * Delete user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Delete result
   */
  const deleteUser = useCallback(
    async (userId) => {
      if (!isAuthenticated || !userId) {
        return { success: false, error: "Not authenticated or missing ID" };
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
          method: "DELETE",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to delete user");
        }

        // Clear selected user if it's the same
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser(null);
        }

        // Refresh user list
        fetchUsers(currentPage, pageSize);

        setLoading(false);
        return { success: true };
      } catch (err) {
        console.error("Delete user error:", err);
        setError(err.message || "Failed to delete user");
        setLoading(false);
        return { success: false, error: err.message };
      }
    },
    [
      isAuthenticated,
      getAuthHeaders,
      selectedUser,
      currentPage,
      pageSize,
      fetchUsers,
    ]
  );

  /**
   * Fetch pending user requests
   * @returns {Promise<Object>} Fetch result
   */
  const fetchPendingRequests = useCallback(async () => {
    if (!isAuthenticated) {
      return { success: false, error: "Not authenticated" };
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithCORS(`${API_BASE_URL}/pending-requests`, {
        method: "GET",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch pending requests"
        );
      }

      const data = await response.json();
      setPendingRequests(data.pendingRequests || []);

      setLoading(false);
      return { success: true, data };
    } catch (err) {
      console.error("Fetch pending requests error:", err);
      setError(err.message || "Failed to fetch pending requests");
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, [isAuthenticated, getAuthHeaders]);

  /**
   * Process pending request
   * @param {string} requestId - Request ID
   * @param {string} action - Action (approve/reject)
   * @param {Object} data - Additional data
   * @returns {Promise<Object>} Process result
   */
  const processPendingRequest = useCallback(
    async (requestId, action, data = {}) => {
      if (!isAuthenticated || !requestId) {
        return { success: false, error: "Not authenticated or missing ID" };
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetchWithCORS(
          `${API_BASE_URL}/pending-requests/${requestId}/${action}`,
          {
            method: "POST",
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Failed to ${action} request`);
        }

        // Refresh pending requests
        await fetchPendingRequests();

        setLoading(false);
        return { success: true };
      } catch (err) {
        console.error(`Process request error (${action}):`, err);
        setError(err.message || `Failed to ${action} request`);
        setLoading(false);
        return { success: false, error: err.message };
      }
    },
    [isAuthenticated, getAuthHeaders, fetchPendingRequests]
  );

  // Load cached users on mount
  useEffect(() => {
    if (cachedUsers && cachedUsers.length > 0 && !isCacheExpired()) {
      setUsers(cachedUsers);
    }
  }, [cachedUsers, isCacheExpired]);

  // Users context value
  const value = {
    users,
    selectedUser,
    setSelectedUser,
    pendingRequests,
    loading,
    error,
    pagination: {
      currentPage,
      totalPages,
      pageSize,
      setCurrentPage,
      setPageSize,
    },
    fetchUsers,
    fetchUserById,
    createUser,
    updateUser,
    deleteUser,
    fetchPendingRequests,
    processPendingRequest,
    clearCache: removeCachedUsers,
  };

  return (
    <UsersContext.Provider value={value}>{children}</UsersContext.Provider>
  );
};

export default UsersContext;
